import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { SearchableSelect, SearchableSelectOption } from 'components';
import { useFleetFiles } from 'hooks';
import { useCustomerAPI } from 'hooks/use-customer-api';
import { Customer } from 'types/customer';
import * as FleetFile from 'types/fleet-files';

/*
 * Links one account to a fleet customer code: the state and the field, without any page or dialog
 * around them. The customer-details page and the Fleet Files grid's dialog both use this, so an
 * account's link is edited the same way from either place.
 *
 * Everything is scoped to the ACCOUNT's own location, never the header's: the codes shown are the
 * account's location's, and the save sends no location at all — the server reads it from the
 * account (F08). Several accounts may share a code, so a code already used by a sibling is offered
 * and the siblings are listed, rather than refused.
 */
export const useAccountLink = (customerId: number) => {
  const { getCustomerById } = useCustomerAPI();
  const { getCustomerLinks, putCustomerLink } = useFleetFiles();

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [links, setLinks] = useState<FleetFile.CustomerLink[]>([]);
  const [selectedCode, setSelectedCode] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const locationId = customer?.location?.id;

  useEffect(() => {
    if (isNaN(customerId)) return;
    getCustomerById(customerId).then(setCustomer);
  }, [customerId, getCustomerById]);

  useEffect(() => {
    if (!locationId) return;
    let current = true;
    getCustomerLinks(locationId)
      .then(loaded => {
        if (!current) return;
        setLinks(loaded);
        setSelectedCode(loaded.find(l => l.customers.some(c => c.id === customerId))?.code ?? null);
      })
      .catch(e => current && setError(e instanceof Error ? e.message : 'Fleet file codes could not be loaded'));
    return () => {
      current = false;
    };
  }, [customerId, getCustomerLinks, locationId]);

  const options: SearchableSelectOption[] = useMemo(
    () => links.map(({ code, name }) => ({ label: `${code}. ${name}`, value: `${code}` })),
    [links]
  );

  /* the other accounts on the selected code — shown, never a reason to refuse */
  const siblings = useMemo(
    () => links.find(l => l.code === selectedCode)?.customers.filter(c => c.id !== customerId) ?? [],
    [customerId, links, selectedCode]
  );

  const select = useCallback((code?: string) => {
    setError(null);
    setSelectedCode(code ? Number(code) : null);
  }, []);

  /*
   * Resolves true once committed. On failure it keeps the selection, records the reason and resolves
   * false, so the caller stays where it is (F09).
   */
  const save = useCallback(async (): Promise<boolean> => {
    if (saving) return false;
    setSaving(true);
    setError(null);
    try {
      await putCustomerLink(customerId, selectedCode);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fleet file code could not be saved');
      return false;
    } finally {
      setSaving(false);
    }
  }, [customerId, putCustomerLink, saving, selectedCode]);

  return { customer, locationId, options, selectedCode, siblings, select, save, saving, error };
};

export type AccountLinkState = ReturnType<typeof useAccountLink>;

/* The field and its messages; the caller supplies the surrounding page or dialog. */
export const AccountLinkField: FC<{ link: AccountLinkState }> = ({ link }) =>
  !!link.customer && !link.locationId ? (
    <p className="text-muted mb-2">This account has no location, so it cannot be linked to a fleet file code.</p>
  ) : (
    <div style={{ maxWidth: 400 }}>
      {!!link.customer?.location?.name && <p className="text-muted mb-2">Codes for {link.customer.location.name}</p>}
      <SearchableSelect
        onClear={link.select}
        onValueSelected={link.select}
        options={link.options}
        value={link.selectedCode === null ? '' : `${link.selectedCode}`}
      />
      {!!link.siblings.length && (
        <p className="text-muted small mt-2" data-cy="fleet-code-siblings">
          Also linked to this code: {link.siblings.map(s => s.name).join(', ')}
        </p>
      )}
      {!!link.error && (
        <p className="text-danger small mt-2" role="alert" data-cy="fleet-link-error">
          {link.error}
        </p>
      )}
    </div>
  );

/* The success message for a committed save, the same wording from either place. */
export const savedMessage = (code: number | null) =>
  code === null ? 'Fleet file code removed' : 'Fleet file code saved';
